import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { ToastService } from '../../../../../shared/services/toast.service';
import { Router } from '@angular/router';
import { PeopleService } from '../../../../../shared/services/people.service';
import { UserService } from '../../../../../shared/services/user.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ShareableInviteComponent } from '../modals/shareable-invite/shareable-invite/shareable-invite.component';
import { BulkInviteComponent } from '../modals/bulk-invite/bulk-invite/bulk-invite.component';
import { constants, PWC_CONSTANTS } from '../../../../../shared/constants/constants';
import { BulkInviteReponseComponent } from '../modals/bulk-invite/bulk-invite-response/bulk-invite-response.component';
import { JobTypeService } from '../../../../../shared/services/job-type.service';
import { SearchMarketService } from '../../../../../shared/services/search-market.service';
import { JobType } from '../../../../../shared/interfaces/settings.interface';
import { SearchMarket } from '../people.interface';
import { UserClientVm } from '../../../../../store/user/user.interface';
import { MultiSelectModule } from 'primeng/multiselect';
import { ConfigurationService } from '../../../../../shared/services/configuration.service';
import { RatingModule } from 'primeng/rating';
import { DropdownModule } from 'primeng/dropdown';
import { SkeletonModule } from 'primeng/skeleton';
import { HelperService } from '../../../../../shared/services/helper.service';
import { PipesModule } from '../../../../../shared/pipes/pipes.module';
import { environment } from '../../../../../../environments/environment';
import { BulkPendingUsersComponent } from '../modals/bulk-pending-users/bulk-pending-users.component';
@Component({
  selector: 'app-invite-candidate',
  standalone: true,
  imports: [
    FloatLabelModule,
    FormsModule,
    InputTextModule,
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    MultiSelectModule,
    DropdownModule,
    RatingModule,
    SkeletonModule,
    PipesModule
  ],
  templateUrl: './invite-candidate.component.html',
  styleUrl: './invite-candidate.component.scss'
})
export class InviteCandidateComponent {

  inviteForm!: FormGroup;
  ref: DynamicDialogRef | undefined;
  jobTypes: Array<JobType> = [];
  searchMarkets: Array<SearchMarket> = [];
  user: UserClientVm | null = null;
  selectedJobTypes = [];
  rankings: any = [];
  isPwcEnabled = false;
  appName = environment.title === PWC_CONSTANTS.ManagedReviewDeployment ? 'PwC' : '';
  bulkPendingCandidate: any;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    public dialogService: DialogService,
    private peopleService: PeopleService,
    private userService: UserService,
    private toastService: ToastService,
    private jobTypeService: JobTypeService,
    private searchMarketService: SearchMarketService,
    private configurationService: ConfigurationService,
    private helperService: HelperService
  ) { }

  async ngOnInit() {
    this.isPwcEnabled = this.configurationService.getPwcFlag();
    this.initializeForm();
    if (this.isPwcEnabled) {
      this.inviteForm.get('secondaryPayRate')?.valueChanges.subscribe(value => {
        const primaryPayRate = this.inviteForm.get('primaryPayRate');
        if (value) {
          primaryPayRate?.clearValidators();
        } else {
          primaryPayRate?.setValidators([Validators.required]);
        }
        primaryPayRate?.updateValueAndValidity();
      });
    }
    this.user = this.userService.getSessionUser();
    this.getFirmJobType();
    this.getFirmSearchMarkets();
    this.getRankings();
    this.getBulkPendingUsers();
  }

  private initializeForm() {
    let formObj: any = {
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.pattern(constants.emailRegex)]],
      phone: [''],
      jobTypes: [[]],
      rating: [`${constants.defaultRatingValue}`]
    };

    if (this.isPwcEnabled) {
      formObj.pwcEmail = ['', [Validators.required, Validators.pattern(constants.emailRegex)]];
      formObj.primaryPayRate = ['', [Validators.required]];
      formObj.secondaryPayRate = [''];
      formObj.ranking = ['', [Validators.required]];
      formObj.guid = ['', [Validators.required]];
      formObj.contractorNumber = ['', [Validators.required]];
      formObj.secondaryContractorNumber = [''];
    }

    this.inviteForm = this.fb.group(formObj);
  }

  getFirmJobType() {
    const firmId = this.user?.firm._id || '';
    this.jobTypeService.getJobTypeDropdownListingByFirmId(firmId).subscribe({
      next: (jobTypes) => {
        this.jobTypes = jobTypes.filter((jobType: any) => !jobType.isArchived && !jobType.isDeleted);
        if(this.user?.firm?.isSingleJobTypeEnabled && this.user?.firm?.selectedSingleJobTypeId){
          const selectedJobType = this.jobTypes.find((type) => type._id.toString() === this.user?.firm?.selectedSingleJobTypeId);
          this.inviteForm.controls['jobTypes'].setValue([selectedJobType]);
        }
      },
    });
  }

  getFirmSearchMarkets() {
    this.searchMarketService
      .getFirmSearchMarkets(this.user?.firm._id || '')
      .subscribe({
        next: (searchMarkets) => {
          this.searchMarkets = searchMarkets;
        },
      });
  }

  getRankings() {
    Object.values(constants.userRankings).forEach((val) => {
      this.rankings.push({id: val, value: val})
    });
  }

  async getBulkPendingUsers(){
    this.bulkPendingCandidate = await this.peopleService.getBulkPendingUsers({
      offset: 0,
      limit: 10
    });
  }

  isJobTypeAvailable(){
    return this.jobTypes && this.jobTypes.length;
  }

  isSearchMarketAvailable(){
    return this.searchMarkets && this.searchMarkets.length;
  }

  validatedInvite(): boolean{

    if(!this.isJobTypeAvailable() && !this.isSearchMarketAvailable()){
      this.toastService.showError('Please add at least one job type and search market to invite candidate');
      return false;
    }

    if(!this.isJobTypeAvailable()){
      this.toastService.showError('Please add at least one job type to invite candidate');
      return false;
    }
    if(!this.isSearchMarketAvailable()){
      this.toastService.showError('Please add at least one search market to invite candidate');
      return false;
    }

    return true;
  }

  onSubmit() {
    if(!this.validatedInvite()){
      return;
    }
    if (this.inviteForm.valid) {
      let data = {
        ...this.inviteForm.value,
        phone: this.formatValue(this.inviteForm.controls['phone'].value),
        jobTypes: this.inviteForm.value.jobTypes ? this.inviteForm.value.jobTypes.map((jobType: any) => jobType._id) : [],
        ranking: this.isPwcEnabled ? this.inviteForm.value.ranking.id : ''
      };
      let body = {
        firmId: this.userService.getUserFirmId(),
        users: [data]
      };
      this.peopleService.inviteCandidates(body).subscribe((response: any) => {
          this.toastService.showSuccess('Invite sent successfully');
          this.inviteForm.reset();
      });
    }
  }

  back() {
    this.router.navigate([`/client/people/all`]);
  }

  showShareableInviteDialog($event: Event) {
    if(!this.validatedInvite()){
      return;
    }
    $event.stopPropagation();
    this.ref = this.dialogService.open(ShareableInviteComponent, {
      header: 'Shareable invite link',
      width: '30vw',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      }
    });
  }

  showBulkPendingUsers() {
    this.dialogService.open(BulkPendingUsersComponent, {
      header: 'Bulk Invited Candidates',
      width: '80vw',
      contentStyle: { overflow: 'auto', 'min-height': '250px' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      }
    });
  }

  showBulkInviteDialog() {
    if(!this.validatedInvite()){
      return;
    }
    const bulkInviteRef: DynamicDialogRef = this.dialogService.open(BulkInviteComponent, {
      header: 'Bulk Invite Candidates',
      width: '80vw',
      contentStyle: { overflow: 'auto' },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      }
    });

    bulkInviteRef.onClose.subscribe((response: any) => {
      if(response){
        this.toastService.showSuccess('Your invite for users is in progress. To see the users, you can click on Pending Bulk Users.');
        this.getBulkPendingUsers();
      }
    });

  }

  getFormValue(value: string) {
    return this.inviteForm.get(value);
  }

  formatContact(event: any): void {
    const formattedInput = this.helperService.formatPhone(event.target.value);
    event.target.value = formattedInput;

    const controlName = event.target.getAttribute('formcontrolname');
    if (controlName && this.inviteForm) {
      this.inviteForm.get(controlName)?.setValue(formattedInput, { emitEvent: false });
    }
  }

  formatValue(value: string): string {
    if(!value) return '';
    return value.replace(/-/g, '');
  }

  limitDecimal(event: any) {
    const value = event.target.value;
    if (value && value.indexOf('.') >= 0) {
      // keep only 2 decimals
      event.target.value = value.replace(/^(\d+)\.(\d{0,2}).*$/, '$1.$2');
      // Optionally update the form control as well:
      const controlName = event.target.getAttribute('formcontrolname');
      if (controlName) {
        this.inviteForm.get(controlName)?.setValue(event.target.value, { emitEvent: false });
      }
    }
  }
}
